import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Pencil, RotateCcw, Trash2 } from "lucide-react";
import api, { fmtPrice, formatApiError, raceTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ResultEditor } from "@/components/ResultEditor";
import { ResultsCsvButton } from "@/components/ResultsCsvButton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const CONFIRM = {
  reset: { title: "Reset results tracking?", text: "Winners & Places will start again from now and today's results so far will be hidden. Past races stay in your Race Program and Final Sheets.", ok: "Reset tracking", url: "/results/reset", word: "RESET" },
  purge: { title: "Delete past races?", text: "Every saved race before today is deleted from the Race Program, Final Sheets and results. This can't be undone.", ok: "Delete past races", url: "/races/clear-past" },
};

const ConfirmAction = ({ kind, onClose, onDone }) => {
  const c = CONFIRM[kind];
  const [typed, setTyped] = useState("");
  useEffect(() => setTyped(""), [kind]);
  const locked = !!c?.word && typed.trim() !== c.word;
  const run = async () => {
    if (locked) return;
    try {
      const { data } = await api.post(c.url);
      toast.success(kind === "reset" ? "Results tracking reset" : `${data.deleted} past race(s) deleted`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <AlertDialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      {c && <AlertDialogContent data-testid={`results-${kind}-dialog`}>
        <AlertDialogHeader><AlertDialogTitle>{c.title}</AlertDialogTitle><AlertDialogDescription>{c.text}</AlertDialogDescription></AlertDialogHeader>
        {c.word && (
          <label className="block text-sm text-slate-300">Type <span className="font-mono font-bold text-rose-300">{c.word}</span> to confirm
            <input autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} data-testid={`results-${kind}-input`}
              className="mt-2 w-full bg-[#0F172A] border border-slate-700 rounded px-3 py-2 font-mono text-white focus:border-rose-500 outline-none" />
          </label>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`results-${kind}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={locked} className="bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:pointer-events-none" data-testid={`results-${kind}-confirm`}>{c.ok}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>}
    </AlertDialog>
  );
};
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;

const Stat = ({ label, big, sub, testId }) => (
  <div className="panel p-5" data-testid={testId}>
    <div className="eyebrow min-h-[2lh]">{label}</div>
    <div className="font-display text-4xl font-bold text-emerald-400 mt-1">{big}</div>
    <div className="text-xs num text-slate-400 mt-1">{sub}</div>
  </div>
);

const money = (v) => `${v < 0 ? "-" : v > 0 ? "+" : ""}$${Math.abs(v).toFixed(2)}`;
const BET_TYPES = [
  ["win", "Top selection • $1 win", "won", "avg_win_div", ""],
  ["place", "Top selection • $1 place", "placed", "avg_place_div", " est."],
  ["top3_win", "Top 3 selections • $1 win on each", "won", "avg_win_div", ""],
  ["top3_place", "Top 3 selections • $1 place on each", "placed", "avg_place_div", " est."],
];
const BetRow = ({ rank, scope, label, s, hit, avg, est, testId }) => (
  <div className="panel p-4 flex items-center gap-4" data-testid={testId}>
    <span className="font-display text-xl font-bold text-amber-300 w-6">{rank}</span>
    <div className="flex-1 min-w-0">
      <div className="eyebrow"><span className="text-amber-300/80">{scope}</span> • {label}</div>
      <div className="text-xs num text-slate-400 mt-1">{s[hit]} {hit} from {plural(s.races ?? s.bets, "race")} • avg {s[avg] ? `$${s[avg].toFixed(2)}${est}` : "—"} • ${s.bets.toFixed(2)} staked • ${s.returned.toFixed(2)} returned</div>
    </div>
    <div className="text-right">
      <div className={`font-display text-2xl font-bold ${s.profit < 0 ? "text-rose-400" : "text-emerald-400"}`}>{money(s.profit)}</div>
      <div className="text-xs num text-slate-500">ROI {s.roi_pct}%</div>
    </div>
  </div>
);
const BetRanking = ({ stats }) => {
  const rows = [["preferred", "Preferred"], ["all", "All races"]].flatMap(([id, scope]) =>
    BET_TYPES.map(([k, ...rest]) => [`${id}-${k}`, scope, k === "place" ? stats[id] : stats[id][k], ...rest]))
    .filter(([, , s]) => s)
    .sort((a, b) => b[2].roi_pct - a[2].roi_pct || b[2].profit - a[2].profit);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-rows-4 lg:grid-flow-col gap-3" data-testid="admin-bet-ranking">
      {rows.map(([key, scope, s, label, hit, avg, est], i) => (
        <BetRow key={key} rank={i + 1} scope={scope} label={label} s={s} hit={hit} avg={avg} est={est} testId={`bet-rank-${i + 1}`} />
      ))}
    </div>
  );
};

const Finish = ({ f }) => {
  if (f == null) return <span className="text-slate-600">—</span>;
  const cls = f === 1 ? "bg-amber-400 text-[#080C14]" : f <= 3 ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-800 text-slate-400";
  return <span className={`num inline-grid place-items-center min-w-[26px] h-6 px-1 rounded text-xs font-bold ${cls}`}>{f}</span>;
};

const Row = ({ r, i, onEdit, isAdmin }) => (
  <tr className="row-hover border-b border-slate-800/60" data-testid={`result-row-${i}`}>
    <td className="px-3 py-3 num text-xs whitespace-nowrap text-slate-400">{r.date}<div className="text-amber-400" data-testid={`results-time-${r.id}`}>{r.start_aest ? raceTime(r) : ""}</div></td>
    <td className="px-3 py-3 whitespace-nowrap">{isAdmin ? <Link to={`/race/${r.id}`} state={{ from: "results" }} className="text-white hover:text-emerald-300 transition-colors font-medium">{r.venue} R{r.race_number}</Link> : <span className="text-white font-medium">{r.venue} R{r.race_number}</span>}
      {r.preferred && <div className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">Preferred</div>}</td>
    {r.top3.map((h, j) => (
      <td key={j} className="px-3 py-3 whitespace-nowrap" data-testid={`result-row-${i}-sel-${j + 1}`}>
        <div className="flex items-center gap-2"><Finish f={h.finish} /><span className={j === 0 ? "text-white font-semibold" : "text-slate-300"}>{h.number} {h.name}</span></div>
        <div className="text-[11px] num text-slate-500 ml-8">rated {fmtPrice(h.price)}</div>
      </td>
    ))}
    {Array.from({ length: 3 - r.top3.length }).map((_, j) => <td key={`e${j}`} />)}
    <td className="px-3 py-3 whitespace-nowrap text-sm" data-testid={`result-row-${i}-winner`}>
      {r.winner ? <>{r.winner.number} {r.winner.name} <span className="num text-xs text-slate-500">{r.winner.sp}</span></> : <span className="text-slate-600">Awaiting result</span>}
    </td>
    <td className="px-3 py-3 text-right">
      <button onClick={() => onEdit(r)} data-testid={`result-edit-${i}`} className="text-slate-400 hover:text-emerald-300 transition-colors" title="Edit result"><Pencil className="h-4 w-4" /></button>
    </td>
  </tr>
);

export default function Results() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [scope, setScope] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const load = () => api.get("/results", { params: { scope } }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNow = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/results/fetch");
      if (d.abandoned_removed) toast.info(`${d.abandoned_removed} abandoned race${d.abandoned_removed === 1 ? "" : "s"} removed`);
      if (d.new_results) toast.success(`${d.new_results} new result${d.new_results === 1 ? "" : "s"} added`);
      else toast.info("No new results published yet. Try again a few minutes after the race.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="py-20 grid place-items-center"><div className="loader-bar" /></div>;
  const s = data.summary;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <div className="eyebrow mb-2" data-testid="results-today">Today's races • {new Date().toLocaleDateString("en-AU", { timeZone: "Australia/Brisbane", weekday: "short", day: "2-digit", month: "short" })} • auto-fetched every 30 min &amp; 8:00 PM AEST</div>
          <h1 className="text-4xl sm:text-5xl font-bold" data-testid="results-title">Winners &amp; Places</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && <ResultsCsvButton scope={scope} />}
          <Button variant="outline" onClick={() => setConfirm("reset")} data-testid="results-reset-button"><RotateCcw className="h-4 w-4 mr-2" />Reset tracking</Button>
          <Button variant="outline" onClick={() => setConfirm("purge")} className="text-rose-300 border-rose-500/40 hover:bg-rose-500/10" data-testid="results-purge-button"><Trash2 className="h-4 w-4 mr-2" />Delete past races</Button>
          <Button onClick={fetchNow} disabled={busy} data-testid="results-fetch-button"><Download className="h-4 w-4 mr-2" />{busy ? "Getting results..." : "Get results now"}</Button>
        </div>
      </div>
      <div className="inline-flex bg-[#0F172A] border border-slate-800 rounded p-0.5" data-testid="results-scope">
        {[["preferred", "Preferred Betting Races"], ["all", "All races"]].map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)} data-testid={`results-scope-${k}`}
            className={`px-3 py-1.5 rounded text-xs font-mono transition-colors ${scope === k ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="eyebrow -mb-3" data-testid="results-tracking-from">Running totals since {data.since
        ? `${new Date(data.since).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} AEST`
        : new Date(`${data.tracking_from}T00:00:00+10:00`).toLocaleDateString("en-AU", { timeZone: "Australia/Brisbane", day: "2-digit", month: "short", year: "numeric" })}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl">
        <Stat label="Top selection • Placed (1st–3rd)" big={`${s.top_place_pct}%`} sub={`${s.top_places} placed from ${plural(s.races, "race")}`} testId="stat-top-place" />
        <Stat label="Top 3 selections • Winner" big={`${s.top3_win_pct}%`} sub={`Winner among top 3 in ${s.top3_wins} of ${plural(s.races, "race")}`} testId="stat-top3-win" />
        <Stat label="Average price of Winner if in top 3" big={s.top3_win_avg_sp ? `$${s.top3_win_avg_sp.toFixed(2)}` : "—"} sub={`Average starting price of ${plural(s.top3_win_priced || 0, "winner")} our top 3 found`} testId="stat-top3-avg-sp" />
      </div>
      {isAdmin && data.place_stats && <div className="space-y-3" data-testid="admin-bet-stats">
        <div className="eyebrow text-amber-300">Admin only • ranked 1–8 by ROI • $1 level stakes • win paid at SP, place estimated from SP (8+ starters ¼ odds, 5–7 ⅓ odds 2 places)</div>
        <BetRanking stats={data.place_stats} />
        <div className="eyebrow" data-testid="place-stats-updated">Win / place figures update every time results come in (Get results now, every 30 min &amp; 8:00 PM AEST)</div>
      </div>}
      <div className="panel p-5 scroll-x">
        <table className="w-full text-sm" data-testid="results-table">
          <thead>
            <tr className="eyebrow border-b border-slate-800 text-left">
              {["Date / AEST", "Race", "Top Selection", "2nd Selection", "3rd Selection", "Winner", ""].map((h) => <th key={h} className="px-3 py-2.5 font-normal whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>{data.races.map((r, i) => <Row key={r.id} r={r} i={i} isAdmin={isAdmin} onEdit={setEditing} />)}</tbody>
        </table>
        {data.races.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">{scope === "preferred" ? "No Preferred Betting Races today yet." : "No rated races today yet."}</p>}
      </div>
      <ConfirmAction kind={confirm} onClose={() => setConfirm(null)} onDone={() => { setConfirm(null); load(); }} />
      {editing && <ResultEditor raceId={editing.id} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
